import { useMemo, useState } from 'react'
import ExportBar from './ExportBar'
import { SELECT_CLASS } from './ReportControls'
import ReportTable, { type ReportTableColumn } from './ReportTable'
import { passes, type ColumnSelection } from './columnFilters'
import { getTeamLookups } from './coverageReportStore'
import { toReportRows } from './coverageReport'
import { dailyRosterTable } from './dailyRosterExport'
import { buildDailyRoster, filterDailyRoster, type DailyRosterFilters, type DailyRosterRow } from './dailyRosterFigures'
import { getDailyRoster } from './dailyRosterStore'
import { todayIso } from './reportDates'
import { nextSort, sortRows, type ReportSort, type SortValue } from './reportSort'
import StaffingSummaryTable from './StaffingSummaryTable'
import { filterStaffing, staffingByAssignment, staffingExportSummary, type StaffingFilters } from './staffingSummary'
import { useCoverageData } from './useCoverageData'
import { useLoaded } from './useLoaded'

/**
 * docs/product/reports.md's daily roster: the officers on duty on one date,
 * by unit, shift, and title, with each officer's name and assignment, to
 * export every day.
 */
const SORT_VALUE: Record<string, (row: DailyRosterRow) => SortValue> = {
  unit: (row) => row.unit,
  shift: (row) => row.shift,
  title: (row) => row.title,
  employee: (row) => row.employee,
  assignment: (row) => row.assignment,
}

const EMPTY = { entries: [], placements: new Map(), onApprovedLeave: new Set<string>() }

export default function DailyRoster() {
  const [date, setDate] = useState(todayIso())
  const [filters, setFilters] = useState<DailyRosterFilters>({})
  const [staffingFilters, setStaffingFilters] = useState<StaffingFilters>({})
  const [sort, setSort] = useState<ReportSort>({ column: 'unit', direction: 'asc' })
  const { data, loading, failed } = useLoaded(
    async () => {
      if (!date) return { roster: EMPTY, teams: new Map<string, string>() }
      const [roster, lookups] = await Promise.all([getDailyRoster(date), getTeamLookups()])
      return { roster, teams: lookups.teams }
    },
    { roster: EMPTY, teams: new Map<string, string>() },
    [date],
  )
  const { roster, teams } = data

  const coverage = useCoverageData(date, date)
  // The staffing figures follow the unit and shift filters; a title or a name
  // does not change how many people a shift requires.
  const staffing = useMemo(
    () =>
      staffingByAssignment(
        toReportRows(coverage.rows, coverage.lookups).filter((row) => passes(filters.unit, row.teamId) && passes(filters.shift, row.shift)),
      ),
    [coverage.rows, coverage.lookups, filters.unit, filters.shift],
  )

  const staffingShown = useMemo(() => filterStaffing(staffing, staffingFilters), [staffing, staffingFilters])

  const built = useMemo(() => buildDailyRoster(roster.entries, roster.placements, roster.onApprovedLeave, teams), [roster, teams])
  const filtered = useMemo(() => filterDailyRoster(built, filters), [built, filters])
  // The build order (unit, shift, title, name) holds within a column's ties.
  const rows = useMemo(() => sortRows(filtered, SORT_VALUE[sort.column], sort.direction), [filtered, sort])

  // Each column offers every value in the roster, not only those left by the
  // other columns' filters, so a filter can always be widened again.
  const valuesOf = (pick: (row: DailyRosterRow) => string) => Array.from(new Set(built.map(pick))).sort().map((name) => ({ value: name, label: name }))
  const filterFor = (key: keyof DailyRosterFilters, options: { value: string; label: string }[]) => ({
    options,
    selected: filters[key],
    onChange: (selected: ColumnSelection) => setFilters((current) => ({ ...current, [key]: selected })),
  })
  const units = Array.from(new Set(built.map((row) => row.teamId)))
    .map((id) => ({ value: id, label: teams.get(id) ?? 'Unknown unit' }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const people = Array.from(new Map(built.map((row) => [row.employeeId, row.identifier ? `${row.employee} (${row.identifier})` : row.employee])))
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const columns: ReportTableColumn<DailyRosterRow>[] = [
    { id: 'unit', label: 'Unit', filter: filterFor('unit', units), cell: (row) => row.unit },
    { id: 'shift', label: 'Shift', filter: filterFor('shift', valuesOf((row) => row.shift)), cell: (row) => row.shift },
    { id: 'title', label: 'Title', filter: filterFor('title', valuesOf((row) => row.title)), cell: (row) => row.title },
    { id: 'employee', label: 'Name', filter: filterFor('employee', people), cell: (row) => row.employee },
    { id: 'assignment', label: 'Assignment', filter: filterFor('assignment', valuesOf((row) => row.assignment)), cell: (row) => row.assignment },
  ]

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Date" className="rounded-card border border-line bg-canvas p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-caption font-bold text-ink-muted">
            Date
            <input type="date" value={date} onChange={(event) => {
                setDate(event.target.value)
                setStaffingFilters({})
                setFilters((current) => ({ ...current, employee: undefined }))
              }} className={SELECT_CLASS} />
          </label>
        </div>
      </section>

      <ExportBar
        loading={loading}
        disabled={loading || failed || coverage.loading || coverage.failed || !date || rows.length === 0}
        fileStem={`muster-daily-roster-${date}`}
        table={() => ({ ...dailyRosterTable(rows, { date, filters, teams, generatedAt: new Date() }), summary: staffingExportSummary('Staffing against targets', staffingShown) })}
      />

      <StaffingSummaryTable
        heading="Staffing against targets"
        note="By unit, shift, and assignment for the date, from the staffing targets. The unit and shift filters apply."
        summary={staffing}
        shown={staffingShown}
        filters={staffingFilters}
        onFiltersChange={setStaffingFilters}
        loading={coverage.loading}
        failed={coverage.failed}
        empty="No staffing targets or scheduled officers for this date."
      />

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The daily roster could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : (
        <ReportTable
          label="Daily roster"
          caption="Officers on duty by unit, shift, and title, with name and assignment"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.key}
          sort={sort}
          onSort={(column) => setSort((current) => nextSort(current, column))}
          onClearFilters={() => setFilters({})}
          loading={loading}
          empty="No one is on duty for this date. Choose another date, or clear a filter."
          minWidth={800}
        />
      )}
    </div>
  )
}
